import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ApiService } from './api.service';
import { URL } from '../constants/service-urls';

export interface SupportConversation {
  user_id: number;
  user_name: string;
  user_email?: string;
  phone?: string;
  user_phone?: string;
  org_id: number;
  org_name: string;
  role?: string;
  has_unread?: boolean;
  last_message?: string;
  last_message_time?: string;
  last_message_iso?: string;
}

export interface SupportFaqItem {
  id?: number;
  organization_id?: number;
  question: string;
  answer: string;
  category?: string;
  display_order?: number;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface SupportChatMessage {
  id: string;
  comment_id?: number;
  user_id?: number;
  org_id?: number;
  text: string;
  author_name?: string;
  user_email?: string;
  phone?: string;
  org_name?: string;
  role?: string;
  sender_type: 'admin' | 'user';
  created_at?: string;
  time?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SupportChatService {
  constructor(private api: ApiService) {}

  /**
   * Fetch conversation threads for Admin view
   */
  getConversations(orgId?: number): Observable<SupportConversation[]> {
    const endpoint = orgId && orgId > 0 
      ? `${URL.SUPPORT_CHAT.CONVERSATIONS}?org_id=${orgId}`
      : URL.SUPPORT_CHAT.CONVERSATIONS;

    return this.api.get(endpoint).pipe(
      map((res: any) => {
        const payload = res && res.body !== undefined ? res.body : res;
        if (payload && payload.data && Array.isArray(payload.data)) {
          return payload.data as SupportConversation[];
        }
        if (payload && Array.isArray(payload)) {
          return payload as SupportConversation[];
        }
        return [];
      }),
      catchError(err => {
        console.warn('Could not fetch support conversations from server:', err);
        return of([]);
      })
    );
  }

  /**
   * Fetch message history for a user conversation from Cassandra
   */
  getMessages(userId: number | string, orgId: number | string): Observable<SupportChatMessage[]> {
    return this.api.get(`${URL.SUPPORT_CHAT.MESSAGES}/${userId}/${orgId || 0}`).pipe(
      map((res: any) => {
        const payload = res && res.body !== undefined ? res.body : res;
        if (payload && payload.data && Array.isArray(payload.data)) {
          return payload.data as SupportChatMessage[];
        }
        if (payload && Array.isArray(payload)) {
          return payload as SupportChatMessage[];
        }
        return [];
      }),
      catchError(err => {
        console.warn('Could not fetch messages from Cassandra:', err);
        return of([]);
      })
    );
  }

  /**
   * Send a support chat message to Cassandra
   */
  sendMessage(payload: {
    user_id: number | string;
    organization_id?: number | string;
    user_name: string;
    user_email?: string;
    phone?: string;
    role?: string;
    org_name?: string;
    message_text: string;
    is_admin?: boolean;
  }): Observable<any> {
    const postData = {
      requestBody: {
        user_id: Number(payload.user_id),
        organization_id: Number(payload.organization_id || 0),
        user_name: payload.user_name || 'User',
        user_email: payload.user_email || '',
        phone: payload.phone || '',
        role: payload.role || 'OU',
        org_name: payload.org_name || 'Certiplate',
        message_text: payload.message_text,
        is_admin: !!payload.is_admin
      }
    };

    return this.api.post(URL.SUPPORT_CHAT.SEND_MESSAGE, postData).pipe(
      map((res: any) => {
        const payload = res && res.body !== undefined ? res.body : res;
        return payload && payload.data ? payload.data : payload;
      }),
      catchError(err => {
        console.error('Error sending message:', err);
        throw err;
      })
    );
  }

  /**
   * Delete a support message (System Super Admin only)
   */
  deleteMessage(commentId: string | number, userId: number, orgId: number = 0): Observable<any> {
    const postData = {
      comment_id: commentId,
      user_id: userId,
      organization_id: orgId
    };

    return this.api.post(URL.SUPPORT_CHAT.DELETE_MESSAGE, postData).pipe(
      map((res: any) => {
        const payload = res && res.body !== undefined ? res.body : res;
        return payload || { success: true };
      }),
      catchError(err => {
        console.error('Error deleting message:', err);
        return of({ success: false, error: err?.message || 'Failed to delete message' });
      })
    );
  }

  /**
   * Mark conversation as read
   */
  markConversationAsRead(userId: number, orgId: number = 0): Observable<any> {
    return this.api.post(URL.SUPPORT_CHAT.MARK_READ, {
      user_id: userId,
      organization_id: orgId
    }).pipe(
      catchError(() => of({ success: false }))
    );
  }

  /**
   * Mark conversation as unread
   */
  markConversationAsUnread(userId: number, orgId: number = 0): Observable<any> {
    return this.api.post(URL.SUPPORT_CHAT.MARK_UNREAD, {
      user_id: userId,
      organization_id: orgId
    }).pipe(
      catchError(() => of({ success: false }))
    );
  }

  /**
   * Fetch Support FAQs from PostgreSQL database
   */
  getFaqs(search?: string, orgId?: number): Observable<SupportFaqItem[]> {
    let endpoint = URL.SUPPORT_CHAT.FAQS;
    const params: string[] = [];
    if (orgId !== undefined) {
      params.push(`org_id=${orgId}`);
    }
    if (search && search.trim()) {
      params.push(`search=${encodeURIComponent(search.trim())}`);
    }
    if (params.length > 0) {
      endpoint += `?${params.join('&')}`;
    }

    return this.api.get(endpoint).pipe(
      map((res: any) => {
        const payload = res && res.body !== undefined ? res.body : res;
        if (payload && payload.data && Array.isArray(payload.data)) {
          return payload.data as SupportFaqItem[];
        }
        if (payload && Array.isArray(payload)) {
          return payload as SupportFaqItem[];
        }
        return [];
      }),
      catchError(err => {
        console.warn('Could not fetch support FAQs from server:', err);
        return of([]);
      })
    );
  }

  /**
   * Create a new FAQ in PostgreSQL (System Super Admin / Admin)
   */
  createFaq(faq: {
    question: string;
    answer: string;
    category?: string;
    display_order?: number;
    organization_id?: number;
  }): Observable<any> {
    const postData = {
      requestBody: {
        question: faq.question,
        answer: faq.answer,
        category: faq.category || 'General',
        display_order: faq.display_order || 0,
        organization_id: faq.organization_id !== undefined ? faq.organization_id : -1
      }
    };

    return this.api.post(URL.SUPPORT_CHAT.FAQS, postData).pipe(
      map((res: any) => {
        const payload = res && res.body !== undefined ? res.body : res;
        return payload && payload.data ? payload.data : payload;
      }),
      catchError(err => {
        console.error('Error creating FAQ:', err);
        throw err;
      })
    );
  }

  /**
   * Update an existing FAQ
   */
  updateFaq(id: number, faq: {
    question?: string;
    answer?: string;
    category?: string;
    display_order?: number;
    is_active?: boolean;
  }): Observable<any> {
    const requestBody: any = {};
    if (faq.question !== undefined) requestBody.question = faq.question;
    if (faq.answer !== undefined) requestBody.answer = faq.answer;
    if (faq.category !== undefined) requestBody.category = faq.category;
    if (faq.display_order !== undefined) requestBody.display_order = faq.display_order;
    if (faq.is_active !== undefined) requestBody.is_active = faq.is_active;

    const putData = { requestBody };

    return this.api.put(`${URL.SUPPORT_CHAT.FAQS}/${id}`, putData).pipe(
      map((res: any) => {
        const payload = res && res.body !== undefined ? res.body : res;
        return payload && payload.data ? payload.data : payload;
      }),
      catchError(err => {
        console.error('Error updating FAQ:', err);
        throw err;
      })
    );
  }

  /**
   * Delete an FAQ
   */
  deleteFaq(id: number): Observable<any> {
    return this.api.delete(URL.SUPPORT_CHAT.FAQS, String(id)).pipe(
      map((res: any) => {
        const payload = res && res.body !== undefined ? res.body : res;
        return payload && payload.data ? payload.data : payload;
      }),
      catchError(err => {
        console.error('Error deleting FAQ:', err);
        throw err;
      })
    );
  }

  /**
   * Send invite email to support member / user
   */
  sendInvite(payload: {
    email: string;
    message: string;
    user_id?: number | null;
    organization_id?: number;
    admin_name?: string;
    admin_email?: string;
    org_name?: string;
    app_url?: string;
  }): Observable<any> {
    const postData = {
      requestBody: {
        email: payload.email,
        message: payload.message,
        user_id: payload.user_id,
        organization_id: payload.organization_id,
        admin_name: payload.admin_name,
        admin_email: payload.admin_email,
        org_name: payload.org_name,
        app_url: payload.app_url || window.location.origin
      }
    };

    return this.api.post(URL.SUPPORT_CHAT.SEND_INVITE, postData).pipe(
      map((res: any) => {
        const bodyPayload = res && res.body !== undefined ? res.body : res;
        return bodyPayload && bodyPayload.data ? bodyPayload.data : bodyPayload;
      }),
      catchError(err => {
        console.error('Error sending support chat invite:', err);
        throw err;
      })
    );
  }

  /**
   * Check if an email is authorized to access Chat with Users for a given organization
   */
  checkAccess(email: string, orgId?: number, userId?: number): Observable<{
    success: boolean;
    hasAccess: boolean;
    isSystemSuperAdmin?: boolean;
    reason?: string;
    message?: string;
    validOrgs?: Array<{ user_id: number; organization_id: number; org_name: string }>;
  }> {
    const params: string[] = [];
    if (email) {
      params.push(`email=${encodeURIComponent(email.trim())}`);
    }
    if (orgId !== undefined) {
      params.push(`org_id=${orgId}`);
    }
    if (userId !== undefined) {
      params.push(`user_id=${userId}`);
    }

    const endpoint = params.length > 0
      ? `${URL.SUPPORT_CHAT.CHECK_ACCESS}?${params.join('&')}`
      : URL.SUPPORT_CHAT.CHECK_ACCESS;

    return this.api.get(endpoint).pipe(
      map((res: any) => {
        const payload = res && res.body !== undefined ? res.body : res;
        return payload || { success: false, hasAccess: false };
      }),
      catchError(err => {
        console.warn('Error checking Chat with Users access:', err);
        return of({ success: false, hasAccess: false, reason: 'error' });
      })
    );
  }
}

